/**
 * Product-type management card — 2026-07.
 *
 * Lets admin / export / gt_export CRUD the ``product_types`` dictionary.
 * Sits inside the Settings page. Emits a global refresh via
 * ``refreshProductTypes()`` after every mutation so all open tabs pick up
 * the change without a full reload.
 */
import { useEffect, useState } from "react";
import { Package, Plus, Trash2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "@/components/ui/sonner";
import { refreshProductTypes } from "@/lib/productTypes";

export const ProductTypesCard = () => {
  const { t } = useLanguage();
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState({ name_zh: "", name_en: "", code: "" });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/product-types", { params: { include_disabled: true } });
      setTypes(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const patch = async (code, field, value) => {
    const target = types.find((t) => t.code === code);
    if (!target) return;
    try {
      await api.put(`/product-types/${code}`, { ...target, [field]: value });
      await load();
      refreshProductTypes();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async (code) => {
    if (!window.confirm(t("productTypes.confirmDelete"))) return;
    try {
      await api.delete(`/product-types/${code}`);
      await load();
      refreshProductTypes();
      toast.success(t("productTypes.deleted"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const add = async () => {
    if (!draft.name_zh || !draft.name_en) {
      toast.error(t("productTypes.nameRequired"));
      return;
    }
    try {
      await api.post("/product-types", draft);
      setDraft({ name_zh: "", name_en: "", code: "" });
      await load();
      refreshProductTypes();
      toast.success(t("productTypes.added"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="rounded-lg border border-border bg-card p-5" data-testid="product-types-card">
      <div className="flex items-center gap-2 mb-3">
        <Package size={16} className="text-brand" />
        <h3 className="font-medium">{t("productTypes.title")}</h3>
      </div>
      <p className="text-xs text-muted-foreground mb-4">{t("productTypes.hint")}</p>
      {loading ? (
        <p className="text-sm text-muted-foreground">…</p>
      ) : (
        <div className="space-y-2">
          {types.map((pt) => (
            <div key={pt.code} className="flex items-center gap-2 border border-border rounded-md px-3 py-2" data-testid={`product-type-row-${pt.code}`}>
              <span className="text-xs font-mono text-muted-foreground w-24 shrink-0">{pt.code}</span>
              <Input
                value={pt.name_zh}
                onChange={(e) => setTypes((cur) => cur.map((c) => c.code === pt.code ? { ...c, name_zh: e.target.value } : c))}
                onBlur={(e) => patch(pt.code, "name_zh", e.target.value)}
                className="h-8 text-sm"
                data-testid={`product-type-name-zh-${pt.code}`}
              />
              <Input
                value={pt.name_en}
                onChange={(e) => setTypes((cur) => cur.map((c) => c.code === pt.code ? { ...c, name_en: e.target.value } : c))}
                onBlur={(e) => patch(pt.code, "name_en", e.target.value)}
                className="h-8 text-sm"
                data-testid={`product-type-name-en-${pt.code}`}
              />
              <div className="flex items-center gap-1.5">
                <Switch
                  checked={pt.enabled}
                  onCheckedChange={(v) => patch(pt.code, "enabled", v)}
                  data-testid={`product-type-toggle-${pt.code}`}
                />
                <span className="text-xs text-muted-foreground w-8">
                  {pt.enabled ? t("productTypes.on") : t("productTypes.off")}
                </span>
              </div>
              {pt.is_default ? (
                <span className="text-[10px] text-muted-foreground px-1.5 py-0.5 border border-border rounded">{t("productTypes.defaultBadge")}</span>
              ) : (
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => remove(pt.code)}
                  className="h-8 w-8 text-destructive"
                  data-testid={`product-type-delete-${pt.code}`}
                >
                  <Trash2 size={14} />
                </Button>
              )}
            </div>
          ))}
          {/* Add-new row */}
          <div className="flex items-center gap-2 border border-dashed border-border rounded-md px-3 py-2 mt-3">
            <Input
              placeholder={t("productTypes.codePlaceholder")}
              value={draft.code}
              onChange={(e) => setDraft({ ...draft, code: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, "") })}
              className="h-8 text-sm w-24 font-mono"
              data-testid="new-product-type-code"
            />
            <Input
              placeholder={t("productTypes.nameZh")}
              value={draft.name_zh}
              onChange={(e) => setDraft({ ...draft, name_zh: e.target.value })}
              className="h-8 text-sm"
              data-testid="new-product-type-zh"
            />
            <Input
              placeholder={t("productTypes.nameEn")}
              value={draft.name_en}
              onChange={(e) => setDraft({ ...draft, name_en: e.target.value })}
              className="h-8 text-sm"
              data-testid="new-product-type-en"
            />
            <Button size="sm" onClick={add} data-testid="add-product-type-button" className="h-8 gap-1">
              <Plus size={14} /> {t("common.add")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
